import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { Add, DeleteOutline, Edit, Payment } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ChatbotModal from "../../components/ChatbotModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import { buildWhatsappTabs, getWhatsappTabParams } from "../../utils/connectionsTabs";
import { hasMenuPermission } from "../../layout/MainListItems";

const ALL_AI_PROCESSES = "all-ai-processes";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(10),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
  },
  formControl: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  textField: {
    width: "100%",
  },
  modelInfo: {
    padding: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  modelInfoHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  modelMetaGrid: {
    marginTop: theme.spacing(1),
  },
  modelMetaItem: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0, 0, 0, 0.08)",
    borderRadius: 6,
    background: "#fff",
    minHeight: 64,
  },
  configHint: {
    marginTop: theme.spacing(1),
    display: "block",
  },
  promptBuilder: {
    padding: theme.spacing(2),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    background: "#fff",
  },
  promptBuilderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  promptBuilderTitle: {
    minWidth: 0,
  },
  promptBuilderHeaderActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  promptBuilderSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
  },
  promptBuilderActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  promptBuilderCard: {
    padding: theme.spacing(1.5),
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 6,
    background: "#fafafa",
  },
  promptBuilderCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  aiBehaviorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  aiBehaviorColumn: {
    minWidth: 0,
  },
  sequenceActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  quickAdminHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  quickAdminTable: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 6,
    overflow: "hidden",
  },
  rowFields: {
    display: "grid",
    gridTemplateColumns: "1fr 120px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  btnWrapper: {
    position: "relative",
  },
  fixedSaveBar: {
    position: "fixed",
    right: theme.spacing(3),
    bottom: theme.spacing(2),
    zIndex: 1200,
    padding: theme.spacing(1),
    borderRadius: 6,
    background: "rgba(255, 255, 255, 0.95)",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.16)",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const initialConfig = {
  isActive: false,
  executionMode: "api",
  apiKeyMode: "included",
  apiKey: "",
  model: "gpt-4o-mini",
  temperature: 1,
  maxOutputTokens: 16384,
  textFormat: "text",
  reasoningEffort: "medium",
  verbosity: "medium",
  summary: "auto",
  prompt: "",
  customPrompt: "",
  useCustomPrompt: true,
  waitModeEnabled: true,
  skipMarkAsRead: false,
  directModeEnabled: false,
  waitModeSeconds: 15,
  applyConversationContext: false,
  conversationContextMessages: 10,
  naturalMessageModeEnabled: true,
  useSequencesTool: true,
  useWasheetsTool: true,
  useWebSearchTool: true,
  useAgendaTool: true,
  useOrdersTool: true,
  useWebhookTools: true,
  useContactTool: true,
  useProcessesTool: false,
  selectedProcessIds: [],
  connectionId: "",
  sharedConnectionIds: [],
};

const normalizeConnectionIds = (...values) => {
  const ids = new Set();
  values.forEach(value => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(id => {
        if (id) ids.add(Number(id));
      });
      return;
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          parsed.forEach(id => {
            if (id) ids.add(Number(id));
          });
          return;
        }
      } catch {
        value.split(",").forEach(id => {
          const parsedId = Number(id.trim());
          if (!Number.isNaN(parsedId)) ids.add(parsedId);
        });
        return;
      }
    }
    ids.add(Number(value));
  });
  return Array.from(ids).filter(id => !Number.isNaN(id));
};

const getModelReasoningEfforts = model => {
  const efforts = model?.settings?.reasoningEfforts;
  return Array.isArray(efforts) && efforts.length ? efforts : ["medium"];
};

const getValidReasoningEffort = (model, effort) => {
  const efforts = getModelReasoningEfforts(model);
  if (efforts.includes(effort)) return effort;
  return model?.settings?.reasoningEffort && efforts.includes(model.settings.reasoningEffort)
    ? model.settings.reasoningEffort
    : efforts[0];
};

const getWhatsappName = (connections, connectionId) =>
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.name ||
  connections.find(whatsapp => Number(whatsapp.id) === Number(connectionId))?.number ||
  `Conexión ${connectionId}`;

const formatCreditValue = value => {
  if (value === null || value === undefined || value === "") return "-";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
};

const weekDays = [
  { key: "monday", label: "Lunes" },
  { key: "tuesday", label: "Martes" },
  { key: "wednesday", label: "Miercoles" },
  { key: "thursday", label: "Jueves" },
  { key: "friday", label: "Viernes" },
  { key: "saturday", label: "Sabado" },
  { key: "sunday", label: "Domingo" },
];

const defaultBusinessHours = weekDays.map(day => ({
  ...day,
  enabled: ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.key),
  open: "08:00",
  close: "18:00",
}));

const emptyBusinessHours = weekDays.map(day => ({
  ...day,
  enabled: false,
  open: "08:00",
  close: "18:00",
}));

const NO_SEQUENCE = "no-sequence";

const toSequenceSelectValue = value => (value ? String(value) : NO_SEQUENCE);
const toSequenceApiValue = value => (value === NO_SEQUENCE ? "" : value || "");

const emptyProduct = () => ({
  name: "",
  description: "",
  price: "",
  offerPrice: "",
  variants: [],
  answer: "",
  sequenceId: NO_SEQUENCE,
});

const emptyProductVariant = () => ({
  name: "",
  price: "",
});

const emptyPaymentMethod = {
  id: null,
  name: "",
  description: "",
  instructions: "",
  requiresProof: false,
  isActive: true,
};

const emptyExampleResponse = () => ({
  question: "",
  answer: "",
  sequenceId: NO_SEQUENCE,
});

const emptyPromptSequence = () => ({
  name: "",
  sequenceId: NO_SEQUENCE,
});

const emptyEscalation = () => ({
  escalation: "",
  answer: "",
  sequenceId: NO_SEQUENCE,
});

const emptyKnowledgeBaseItem = () => ({
  question: "",
  site: "",
  answer: "",
});

const initialPromptBuilder = {
  businessName: "",
  businessCategory: "",
  businessSummary: "",
  address: "",
  businessHours: defaultBusinessHours,
  website: "",
  phones: "",
  socialNetworks: "",
  emails: "",
  languages: "Español",
  assistantName: "",
  tone: "Amable y profesional",
  responseLength: "Breve",
  emojiUsage: "Moderado",
  formality: "Cercano y respetuoso",
  salesStyle: "Consultivo",
  purchaseInstructions: "",
  discounts: "",
  shippingPolicy: "",
  returnsPolicy: "",
  products: [emptyProduct()],
  exampleResponses: [emptyExampleResponse()],
  promptSequences: [emptyPromptSequence()],
  escalations: [emptyEscalation()],
  knowledgeBase: [emptyKnowledgeBaseItem()],
  orderFlow: "",
  generalResponseRules: "",
};

const emptyPromptBuilder = {
  ...initialPromptBuilder,
  businessHours: emptyBusinessHours,
  languages: "",
  tone: "",
  responseLength: "",
  emojiUsage: "",
  formality: "",
  salesStyle: "",
};

const defaultPromptBuilder = {
  ...initialPromptBuilder,
  businessName: "Mi Negocio",
  businessCategory: "Tienda online de productos para el hogar, tecnologia basica y accesorios personales",
  businessSummary:
    "Mi Negocio vende productos practicos para uso diario, regalos, accesorios y articulos de tendencia. Atendemos por WhatsApp, confirmamos pedidos, resolvemos dudas, compartimos productos disponibles y ayudamos al cliente a completar su compra de forma clara y rapida.",
  address:
    "San Jose, Costa Rica. Entregas disponibles en GAM y zonas seleccionadas.",
  website: "https://minegocio.com/catalogo",
  phones: "+506 7000-0000",
  socialNetworks:
    "Instagram @minegocio, Facebook Mi Negocio, TikTok @minegocio",
  emails: "mi@negocio.com",
  languages: "Espanol",
  assistantName: "Sofia",
  tone: "Amable, claro, comercial y resolutivo",
  responseLength: "Breve",
  emojiUsage: "Moderado",
  formality: "Semi formal",
  salesStyle:
    "Ayuda primero, vende despues. Recomienda productos solo cuando tenga sentido para la necesidad del cliente.",
  purchaseInstructions:
    "Para tomar un pedido, confirma producto, cantidad, color o variante, nombre del cliente, telefono, direccion exacta, metodo de pago y disponibilidad. Antes de cerrar, resume el pedido y pregunta si desea confirmar.",
  discounts:
    "Hay descuentos ocasionales por compra de 2 o mas unidades. Si el cliente pregunta por descuento, ofrece revisar promociones activas y sugiere productos relacionados.",
  shippingPolicy:
    "En GAM el envio puede ser el mismo dia segun disponibilidad y horario. Fuera de GAM puede tardar 24 a 72 horas. El costo de envio depende de la zona, salvo promociones con envio gratis.",
  returnsPolicy:
    "Los cambios aplican durante las primeras 48 horas despues de recibir el producto, siempre que este sin uso y con empaque original. No se aceptan cambios por dano causado por mal uso.",
  products: [
    {
      name: "Lampara LED recargable de escritorio",
      description:
        "Lampara compacta con tres niveles de intensidad, bateria recargable USB y cuello flexible. Ideal para estudiar, trabajar o leer de noche.",
      price: "CRC 12,900",
      offerPrice: "CRC 9,900",
      answer:
        "Claro, tenemos la lampara LED recargable disponible. Te comparto los detalles principales.",
      sequenceId: NO_SEQUENCE,
    },
    {
      name: "Organizador plegable para ropa",
      description:
        "Caja organizadora para closet, resistente, plegable y facil de mover. Ayuda a ordenar camisetas, pantalones, sabanas o accesorios.",
      price: "CRC 7,500",
      offerPrice: "CRC 5,990",
      answer:
        "Si, tenemos organizadores plegables disponibles. Te comparto sus detalles principales.",
      sequenceId: NO_SEQUENCE,
    },
    {
      name: "Audifonos Bluetooth Urban Sound",
      description:
        "Audifonos inalambricos con estuche cargador, microfono integrado y hasta 5 horas de uso continuo. Compatibles con Android y iPhone.",
      price: "CRC 18,900",
      offerPrice: "CRC 14,900",
      answer:
        "Tenemos audifonos Bluetooth disponibles. Te comparto sus caracteristicas principales.",
      sequenceId: NO_SEQUENCE,
    },
  ],
  exampleResponses: [
    {
      question: "hacen envios",
      answer:
        "Si, hacemos envios. En GAM podemos entregar el mismo dia segun horario y disponibilidad. Para decirte el costo exacto, indicame tu zona o direccion aproximada.",
      sequenceId: NO_SEQUENCE,
    },
    {
      question: "puedo pagar al recibir",
      answer:
        "Si, puedes pagar al recibir en efectivo si tu zona aplica para entrega contra entrega. Tambien aceptamos {{metodo de pago 1}}, {{metodo de pago 2}} y {{metodo de pago 3}}.",
      sequenceId: NO_SEQUENCE,
    },
    {
      question: "tienen garantia",
      answer:
        "Si, nuestros productos tienen garantia por defectos de fabrica. La garantia no cubre danos por golpes, humedad o mal uso.",
      sequenceId: NO_SEQUENCE,
    },
    {
      question: "quiero comprar",
      answer:
        "Perfecto, con gusto te ayudo a completar tu pedido. Primero confirmemos el producto y tus datos de entrega.",
      sequenceId: NO_SEQUENCE,
    },
  ],
  promptSequences: [
    { name: "Video de Producto #1", sequenceId: NO_SEQUENCE },
    { name: "Medidas y detalles de Producto #1", sequenceId: NO_SEQUENCE },
    { name: "Video de Producto #2", sequenceId: NO_SEQUENCE },
    { name: "Medidas y detalles de Producto #2", sequenceId: NO_SEQUENCE },
    { name: "Video de Producto #3", sequenceId: NO_SEQUENCE },
    { name: "Comparativa de Producto #3", sequenceId: NO_SEQUENCE },
    { name: "Iniciar toma de pedido", sequenceId: NO_SEQUENCE },
  ],
  escalations: [
    {
      escalation:
        "El cliente pide hablar con una persona, solicita un reclamo, reporta un problema con un pedido, pide una excepcion de precio, solicita garantia o esta molesto.",
      answer:
        "Claro, voy a pasar tu caso con un asesor para que pueda ayudarte personalmente.",
      sequenceId: NO_SEQUENCE,
    },
    {
      escalation:
        "El cliente desea comprar mas de 5 unidades, solicita precio mayorista o quiere una cotizacion especial.",
      answer:
        "Perfecto, voy a transferirte con ventas para revisar una cotizacion especial.",
      sequenceId: NO_SEQUENCE,
    },
  ],
  knowledgeBase: [
    {
      question: "que productos tienen disponibles",
      site: "https://minegocio.com/catalogo",
      answer: "{{productos disponibles del catalogo}}",
    },
    {
      question: "cuales son las politicas de garantia",
      site: "https://minegocio.com/garantia",
      answer: "{{politicas de garantia del sitio}}",
    },
    {
      question: "cuanto cuesta el envio",
      site: "https://minegocio.com/envios",
      answer: "{{costos y zonas de envio del sitio}}",
    },
  ],
  orderFlow:
    "Cuando el cliente quiera comprar:\n1. Confirma el producto exacto.\n2. Pregunta cantidad y variante si aplica.\n3. Solicita nombre, telefono y direccion exacta.\n4. Pregunta metodo de pago.\n5. Resume el pedido con producto, cantidad, total, envio, direccion y forma de pago.\n6. Pregunta si confirma.\n7. Cuando confirme, registra el pedido en el formulario pedidos usando manage_orders.\n8. Responde con una confirmacion clara y breve.",
  generalResponseRules:
    "- Responde siempre en espanol claro.\n- No inventes precios, disponibilidad, politicas ni datos del cliente.\n- Si falta informacion para continuar, haz una sola pregunta concreta.\n- Si el cliente esta listo para comprar, avanza hacia completar el pedido.\n- Si el cliente esta indeciso, recomienda maximo 2 opciones y explica la diferencia.\n- Si el cliente pide algo fuera del alcance, escala a un asesor.\n- Manten respuestas breves, utiles y orientadas a resolver.",
};

const clonePromptBuilder = builder => JSON.parse(JSON.stringify(builder));

const normalizePromptBuilderSequences = builder => ({
  ...builder,
  products: (builder.products || []).map(product => ({
    ...product,
    sequenceId: toSequenceSelectValue(product.sequenceId),
  })),
  exampleResponses: (builder.exampleResponses || []).map(item => ({
    ...item,
    sequenceId: toSequenceSelectValue(item.sequenceId),
  })),
  promptSequences: (builder.promptSequences || []).map(item => ({
    ...item,
    sequenceId: toSequenceSelectValue(item.sequenceId),
  })),
  escalations: (builder.escalations || []).map(item => ({
    ...item,
    sequenceId: toSequenceSelectValue(item.sequenceId),
  })),
});

const sanitizePromptBuilderSequences = builder => ({
  ...builder,
  products: (builder.products || []).map(product => ({
    ...product,
    sequenceId: toSequenceApiValue(product.sequenceId),
  })),
  exampleResponses: (builder.exampleResponses || []).map(item => ({
    ...item,
    sequenceId: toSequenceApiValue(item.sequenceId),
  })),
  promptSequences: (builder.promptSequences || []).map(item => ({
    ...item,
    sequenceId: toSequenceApiValue(item.sequenceId),
  })),
  escalations: (builder.escalations || []).map(item => ({
    ...item,
    sequenceId: toSequenceApiValue(item.sequenceId),
  })),
});

const parsePromptBuilder = value => {
  if (!value) return emptyPromptBuilder;

  try {
    const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
    const { paymentMethods: _legacyPaymentMethods, ...builderValue } = parsedValue;
    return normalizePromptBuilderSequences({
      ...initialPromptBuilder,
      ...builderValue,
      businessHours: Array.isArray(builderValue.businessHours)
        ? defaultBusinessHours.map(day => ({
            ...day,
            ...(builderValue.businessHours.find(item => item.key === day.key) || {}),
          }))
        : defaultBusinessHours,
      products: Array.isArray(builderValue.products) && builderValue.products.length
        ? builderValue.products
        : [emptyProduct()],
      exampleResponses: Array.isArray(builderValue.exampleResponses) && builderValue.exampleResponses.length
        ? builderValue.exampleResponses
        : [emptyExampleResponse()],
      promptSequences: Array.isArray(builderValue.promptSequences) && builderValue.promptSequences.length
        ? builderValue.promptSequences
        : [emptyPromptSequence()],
      escalations: Array.isArray(builderValue.escalations) && builderValue.escalations.length
        ? builderValue.escalations
        : [emptyEscalation()],
      knowledgeBase: Array.isArray(builderValue.knowledgeBase) && builderValue.knowledgeBase.length
        ? builderValue.knowledgeBase
        : [emptyKnowledgeBaseItem()],
    });
  } catch (err) {
    return emptyPromptBuilder;
  }
};

const addPromptSection = (sections, title, lines) => {
  const content = lines.filter(Boolean).join("\n");
  if (content) sections.push(`${title}\n${content}`);
};

const buildPromptBlock = lines => lines.filter(Boolean).join("\n");

const defaultOrderSettings = {
  currencySymbol: "$",
  decimalPlaces: 2,
};

const parsePromptMoney = value => {
  const raw = String(value || "").replace(/[^\d,.-]/g, "").trim();
  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");
  const decimalSeparator =
    lastComma > lastDot ? "," : lastDot > lastComma ? "." : "";

  if (!decimalSeparator) return Number(raw) || 0;

  const decimalPart = raw.slice(raw.lastIndexOf(decimalSeparator) + 1);
  const separatorIsThousands =
    decimalPart.length === 3 &&
    raw.indexOf(decimalSeparator) === raw.lastIndexOf(decimalSeparator);
  const normalized = separatorIsThousands
    ? raw.replace(/[,.]/g, "")
    : raw
        .replace(decimalSeparator === "," ? /\./g : /,/g, "")
        .replace(decimalSeparator, ".");

  return Number(normalized) || 0;
};

const formatPromptMoney = (value, settings = defaultOrderSettings) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (/[A-Za-z$₡€£¥]/.test(text)) return text;

  const decimalPlaces = Math.min(
    4,
    Math.max(0, Number(settings.decimalPlaces || 0))
  );
  const formatted = parsePromptMoney(text).toLocaleString("es-CR", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return `${settings.currencySymbol || ""}${formatted}`;
};

const buildPromptFromBuilder = (
  builder,
  orderSettings = defaultOrderSettings,
  featureFlags = {}
) => {
  const normalizedBuilder = sanitizePromptBuilderSequences(builder);
  const sections = [];
  const canUseOrdersTool = featureFlags.useOrdersTool !== false;
  const canUseSequencesTool = featureFlags.useSequencesTool !== false;
  const canUseWebSearchTool = featureFlags.useWebSearchTool !== false;

  addPromptSection(sections, "IDENTIDAD DEL NEGOCIO", [
    normalizedBuilder.businessName && `Nombre comercial: ${normalizedBuilder.businessName}`,
    normalizedBuilder.businessCategory && `Categoria: ${normalizedBuilder.businessCategory}`,
    normalizedBuilder.businessSummary && `Descripcion: ${normalizedBuilder.businessSummary}`,
    normalizedBuilder.address && `Direcciones: ${normalizedBuilder.address}`,
    Array.isArray(normalizedBuilder.businessHours) &&
      normalizedBuilder.businessHours.some(day => day.enabled) &&
      `Horario:\n${normalizedBuilder.businessHours
        .filter(day => day.enabled)
        .map(day => `- ${day.label}: ${day.open} a ${day.close}`)
        .join("\n")}`,
    normalizedBuilder.website && `Sitios web: ${normalizedBuilder.website}`,
    normalizedBuilder.phones && `Telefonos: ${normalizedBuilder.phones}`,
    normalizedBuilder.socialNetworks && `Redes sociales: ${normalizedBuilder.socialNetworks}`,
    normalizedBuilder.emails && `Correos: ${normalizedBuilder.emails}`,
    normalizedBuilder.languages && `Idiomas soportados: ${normalizedBuilder.languages}`,
  ]);

  addPromptSection(sections, "PERSONALIDAD DE LA IA", [
    normalizedBuilder.assistantName && `Nombre del asistente: ${normalizedBuilder.assistantName}`,
    normalizedBuilder.tone && `Tono: ${normalizedBuilder.tone}`,
    normalizedBuilder.responseLength && `Longitud de respuesta: ${normalizedBuilder.responseLength}`,
    normalizedBuilder.emojiUsage && `Uso de emojis: ${normalizedBuilder.emojiUsage}`,
    normalizedBuilder.formality && `Formalidad: ${normalizedBuilder.formality}`,
    normalizedBuilder.salesStyle && `Estilo comercial: ${normalizedBuilder.salesStyle}`,
  ]);

  if (canUseOrdersTool) {
    const purchaseInfo = [
    normalizedBuilder.purchaseInstructions &&
      buildPromptBlock(["INSTRUCCIONES DE COMPRA", normalizedBuilder.purchaseInstructions]),
    normalizedBuilder.discounts && buildPromptBlock(["DESCUENTOS", normalizedBuilder.discounts]),
    normalizedBuilder.shippingPolicy &&
      buildPromptBlock(["POLITICA DE ENVIO", normalizedBuilder.shippingPolicy]),
    normalizedBuilder.returnsPolicy &&
      buildPromptBlock([
        "POLITICA DE DEVOLUCIONES Y CAMBIOS",
        normalizedBuilder.returnsPolicy,
      ]),
  ]
    .filter(Boolean)
    .join("\n\n");

  addPromptSection(sections, "INFORMACION DE COMPRA", [purchaseInfo]);

  const products = (normalizedBuilder.products || []).filter(
    product =>
      product.name ||
      product.description ||
      product.price ||
      product.offerPrice ||
      product.answer ||
      (canUseSequencesTool && product.sequenceId)
  );
    addPromptSection(sections, "PRODUCTOS", [
    products.length &&
      products
        .map((product, index) =>
          buildPromptBlock([
            `Producto #${index + 1}`,
            product.name && `Nombre: ${product.name}`,
            product.description && `Descripcion: ${product.description}`,
            product.price && `Precio: ${formatPromptMoney(product.price, orderSettings)}`,
            product.offerPrice &&
              `Precio oferta: ${formatPromptMoney(product.offerPrice, orderSettings)}`,
            Array.isArray(product.variants) &&
              product.variants.some(variant => variant.name || variant.price) &&
              `Variaciones:\n${product.variants
                .filter(variant => variant.name || variant.price)
                .map(
                  variant =>
                    `- ${variant.name || "Variacion"}${
                      variant.price
                        ? `: ${formatPromptMoney(variant.price, orderSettings)}`
                        : ""
                    }`
                )
                .join("\n")}`,
            canUseSequencesTool &&
              product.sequenceId &&
              `IA Ejecuta Secuencia: ID ${product.sequenceId}`,
            product.answer && `Responde solo con: ${product.answer}`,
          ])
        )
        .join("\n\n"),
    canUseSequencesTool &&
      products.some(product => product.sequenceId) &&
      "\nSi un producto tiene \"IA Ejecuta Secuencia\", ejecuta la secuencia usando la función execute_sequence y luego responde exactamente con el texto indicado.",
    ]);
  }

  const exampleResponses = (normalizedBuilder.exampleResponses || []).filter(
    item => item.question || item.answer || (canUseSequencesTool && item.sequenceId)
  );
  addPromptSection(sections, "PREGUNTAS FRECUENTES", [
    exampleResponses.length &&
      exampleResponses
        .map(item =>
          buildPromptBlock([
            item.question && `Cliente: "${item.question}"`,
            canUseSequencesTool &&
              item.sequenceId &&
              `IA Ejecuta Secuencia: ID ${item.sequenceId}`,
            item.answer && `Responde solo con: "${item.answer}"`,
          ])
        )
        .join("\n\n"),
    canUseSequencesTool &&
      exampleResponses.some(item => item.sequenceId) &&
      "\nSi una pregunta frecuente tiene \"IA Ejecuta Secuencia\", ejecuta la secuencia usando la función execute_sequence y luego responde exactamente con el texto indicado.",
  ]);

  if (canUseSequencesTool) {
    const promptSequences = (normalizedBuilder.promptSequences || []).filter(
    item => item.name || item.sequenceId
  );
  addPromptSection(sections, "SECUENCIAS", [
    promptSequences.length &&
      promptSequences
        .map(item =>
          buildPromptBlock([
            item.name && `Nombre: ${item.name}`,
            item.sequenceId && `IA Ejecuta Secuencia: ID ${item.sequenceId}`,
          ])
        )
        .join("\n\n"),
    promptSequences.some(item => item.sequenceId) &&
      "\nSi una secuencia tiene \"IA Ejecuta Secuencia\", ejecuta la secuencia usando la función execute_sequence y luego responde exactamente con el texto indicado.",
    ]);
  }

  const escalations = (normalizedBuilder.escalations || []).filter(
    item => item.escalation || item.answer || (canUseSequencesTool && item.sequenceId)
  );
  addPromptSection(sections, "ESCALAMIENTOS", [
    escalations.length &&
      escalations
        .map(item =>
          buildPromptBlock([
            item.escalation && `Escalar cuando: ${item.escalation}`,
            canUseSequencesTool &&
              item.sequenceId &&
              `IA Ejecuta Secuencia: ID ${item.sequenceId}`,
            item.answer && `Responde solo con: ${item.answer}`,
          ])
        )
        .join("\n\n"),
    canUseSequencesTool &&
      escalations.some(item => item.sequenceId) &&
      "\nSi un escalamiento tiene \"IA Ejecuta Secuencia\", ejecuta la secuencia usando la función execute_sequence y luego responde exactamente con el texto indicado.",
  ]);

  if (canUseWebSearchTool) {
    const knowledgeBase = (normalizedBuilder.knowledgeBase || []).filter(
    item => item.question || item.site || item.answer
  );
  addPromptSection(sections, "BASE DE CONOCIMIENTOS", [
    knowledgeBase.length &&
      knowledgeBase
        .map(item =>
          buildPromptBlock([
            item.question && `Cliente: ${item.question}`,
            item.site && `IA Accede: ${item.site}`,
            item.answer && `Responde solo con: ${item.answer}`,
          ])
        )
        .join("\n\n"),
    knowledgeBase.some(item => item.site) &&
      "\nSi una base de conocimiento tiene \"IA Accede\", accede a la url usando la función read_url y luego responde exactamente con el texto indicado.",
    ]);
  }

  if (canUseOrdersTool) {
    addPromptSection(sections, "FLUJO RECOMENDADO PARA PEDIDOS", [
      normalizedBuilder.orderFlow,
    ]);
  }

  addPromptSection(sections, "REGLAS GENERALES DE RESPUESTA", [
    normalizedBuilder.generalResponseRules,
  ]);

  return sections.join("\n\n").trim();
};

const Ai = () => {
  const classes = useStyles();
  const { user, impersonation } = useContext(AuthContext);
  const isSuperAdmin =
    user?.profile === "superadmin" ||
    impersonation?.originalUser?.profile === "superadmin";
  const canUseAgenda = hasMenuPermission(user, "agenda");
  const canUseOrders = hasMenuPermission(user, "orders");
  const canUseWebhooks = hasMenuPermission(user, "webhooks");
  const canUseProcesses = hasMenuPermission(user, "processes");
  const [config, setConfig] = useState(initialConfig);
  const [promptBuilder, setPromptBuilder] = useState(initialPromptBuilder);
  const [models, setModels] = useState([]);
  const [aiIncludedCreditUsage, setAiIncludedCreditUsage] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalContext, setSequenceModalContext] = useState(null);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [orderSettings, setOrderSettings] = useState(defaultOrderSettings);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
  const [paymentMethodForm, setPaymentMethodForm] = useState(emptyPaymentMethod);
  const [deletingPaymentMethod, setDeletingPaymentMethod] = useState(null);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [washeetDocuments, setWasheetDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );
  const selectedWhatsappIsOrphan = Boolean(selectedWhatsappTab?.orphan);
  const canUseWasheets = washeetDocuments.length > 0;

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups"),
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState =>
          prevState || tabs[0]?.value || ""
        );
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchConnections();
  }, []);

  useEffect(() => {
    if (!selectedConnectionId) {
      setConfig(initialConfig);
      setAiIncludedCreditUsage(null);
      return;
    }

    const fetchConfig = async () => {
      setLoading(true);
      try {
        const connectionParams = getWhatsappTabParams(selectedConnectionId);
        const [
          { data },
          { data: orderSettingsData },
          { data: paymentMethodData },
          { data: processesData },
          { data: washeetData },
        ] = await Promise.all([
          api.get("/ai/config", {
            params: connectionParams,
          }),
          canUseOrders ? api.get("/orders/settings") : Promise.resolve({ data: { settings: defaultOrderSettings } }),
          selectedWhatsappIsOrphan || !canUseOrders
            ? Promise.resolve({ data: { paymentMethods: [] } })
            : api.get("/orders/payment-methods", { params: connectionParams }),
          selectedWhatsappIsOrphan || !canUseProcesses
            ? Promise.resolve({ data: { processes: [] } })
            : api.get("/ai-processes", { params: connectionParams }),
          api.get("/washeets/documents").catch(() => ({ data: { documents: [] } })),
        ]);
        const sharedConnectionIds = selectedWhatsappIsOrphan
          ? []
          : normalizeConnectionIds(
              data.config?.sharedConnectionIds,
              data.config?.connectionId,
              selectedConnectionId
            );
        setConfig({
          ...initialConfig,
          ...(data.config || {}),
          useAgendaTool: canUseAgenda && data.config?.useAgendaTool !== false,
          useOrdersTool: canUseOrders && data.config?.useOrdersTool !== false,
          useWasheetsTool: (washeetData.documents || []).length > 0 && data.config?.useWasheetsTool !== false,
          useWebhookTools: canUseWebhooks && data.config?.useWebhookTools !== false,
          useProcessesTool: canUseProcesses && data.config?.useProcessesTool === true,
          connectionId: data.config?.connectionId || selectedConnectionId,
          sharedConnectionIds,
          selectedProcessIds: canUseProcesses
            ? normalizeConnectionIds(data.config?.selectedProcessIds)
            : [],
        });
        setPromptBuilder(parsePromptBuilder(data.config?.promptBuilder));
        setOrderSettings(orderSettingsData.settings || defaultOrderSettings);
        setPaymentMethods(paymentMethodData.paymentMethods || []);
        setAvailableProcesses(processesData.processes || []);
        setWasheetDocuments(washeetData.documents || []);
        setModels(data.models || []);
        setAiIncludedCreditUsage(data.aiIncludedCreditUsage || null);
        await fetchSequences();
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [canUseOrders, canUseProcesses, canUseAgenda, canUseWebhooks, selectedConnectionId, selectedWhatsappIsOrphan]);

  const fetchSequences = async () => {
    if (!selectedConnectionId) {
      setSequences([]);
      return [];
    }

    const { data } = await api.get("/chatbot/sequences", {
      params: {
        all: true,
        pageNumber: 1,
        ...getWhatsappTabParams(selectedConnectionId),
      },
    });
    const nextSequences = data.sequences || [];
    setSequences(nextSequences);
    return nextSequences;
  };

  const fetchPaymentMethods = async () => {
    if (!selectedConnectionId || selectedWhatsappIsOrphan) {
      setPaymentMethods([]);
      return [];
    }

    const { data } = await api.get("/orders/payment-methods", {
      params: getWhatsappTabParams(selectedConnectionId),
    });
    setPaymentMethods(data.paymentMethods || []);
    return data.paymentMethods || [];
  };

  const openPaymentMethodModal = method => {
    setPaymentMethodForm(
      method
        ? { ...emptyPaymentMethod, ...method }
        : { ...emptyPaymentMethod, connectionId: selectedConnectionId }
    );
    setPaymentMethodModalOpen(true);
  };

  const savePaymentMethod = async () => {
    try {
      const payload = {
        ...paymentMethodForm,
        connectionId: paymentMethodForm.connectionId || selectedConnectionId,
      };
      if (paymentMethodForm.id) {
        await api.put(`/orders/payment-methods/${paymentMethodForm.id}`, payload);
      } else {
        await api.post("/orders/payment-methods", payload);
      }
      toast.success("Metodo de pago guardado");
      setPaymentMethodModalOpen(false);
      setPaymentMethodForm(emptyPaymentMethod);
      await fetchPaymentMethods();
    } catch (err) {
      toastError(err);
    }
  };

  const deletePaymentMethod = async () => {
    if (!deletingPaymentMethod) return;
    try {
      await api.delete(`/orders/payment-methods/${deletingPaymentMethod.id}`);
      toast.success("Metodo de pago eliminado");
      setDeletingPaymentMethod(null);
      await fetchPaymentMethods();
    } catch (err) {
      toastError(err);
    }
  };

  const handleChange = event => {
    const { name, value, type, checked } = event.target;
    if (name === "model") {
      const nextModel = models.find(model => model.id === value);
      setConfig(prevConfig => ({
        ...prevConfig,
        ...(nextModel?.settings || {}),
        reasoningEffort: getValidReasoningEffort(
          nextModel,
          nextModel?.settings?.reasoningEffort || prevConfig.reasoningEffort
        ),
        model: value,
      }));
      return;
    }

    setConfig(prevConfig => ({
      ...prevConfig,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePromptBuilderChange = event => {
    const { name, value } = event.target;
    setPromptBuilder(prevBuilder => ({ ...prevBuilder, [name]: value }));
  };

  const updateBusinessHour = (dayKey, field, value) => {
    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      businessHours: prevBuilder.businessHours.map(day =>
        day.key === dayKey ? { ...day, [field]: value } : day
      ),
    }));
  };

  const updatePromptBuilderList = (listName, index, field, value) => {
    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      [listName]: prevBuilder[listName].map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addProductVariant = productIndex => {
    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      products: prevBuilder.products.map((product, index) =>
        index === productIndex
          ? {
              ...product,
              variants: [...(product.variants || []), emptyProductVariant()],
            }
          : product
      ),
    }));
  };

  const updateProductVariant = (productIndex, variantIndex, field, value) => {
    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      products: prevBuilder.products.map((product, index) =>
        index === productIndex
          ? {
              ...product,
              variants: (product.variants || []).map((variant, currentIndex) =>
                currentIndex === variantIndex
                  ? { ...variant, [field]: value }
                  : variant
              ),
            }
          : product
      ),
    }));
  };

  const removeProductVariant = (productIndex, variantIndex) => {
    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      products: prevBuilder.products.map((product, index) =>
        index === productIndex
          ? {
              ...product,
              variants: (product.variants || []).filter(
                (variant, currentIndex) => currentIndex !== variantIndex
              ),
            }
          : product
      ),
    }));
  };

  const addPromptBuilderListItem = listName => {
    const emptyItemByList = {
      products: emptyProduct,
      escalations: emptyEscalation,
      knowledgeBase: emptyKnowledgeBaseItem,
      exampleResponses: emptyExampleResponse,
      promptSequences: emptyPromptSequence,
    };
    const createEmptyItem = emptyItemByList[listName] || emptyExampleResponse;

    setPromptBuilder(prevBuilder => ({
      ...prevBuilder,
      [listName]: [...prevBuilder[listName], createEmptyItem()],
    }));
  };

  const removePromptBuilderListItem = (listName, index) => {
    const emptyItemByList = {
      products: emptyProduct,
      escalations: emptyEscalation,
      knowledgeBase: emptyKnowledgeBaseItem,
      exampleResponses: emptyExampleResponse,
      promptSequences: emptyPromptSequence,
    };
    const createEmptyItem = emptyItemByList[listName] || emptyExampleResponse;

    setPromptBuilder(prevBuilder => {
      const nextItems = prevBuilder[listName].filter((item, itemIndex) => itemIndex !== index);
      return {
        ...prevBuilder,
        [listName]: nextItems.length ? nextItems : [createEmptyItem()],
      };
    });
  };

  const openCreateSequence = (listName, index) => {
    if (selectedWhatsappIsOrphan) {
      toast.error("Conecta una nueva conexion para crear secuencias.");
      return;
    }

    setSequenceModalContext({ listName, index });
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (listName, index, sequenceId) => {
    if (!sequenceId || sequenceId === NO_SEQUENCE) return;
    if (selectedWhatsappIsOrphan) {
      toast.error("Conecta una nueva conexion para editar secuencias.");
      return;
    }

    setSequenceModalContext({ listName, index });
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const formatSequenceOption = sequence =>
    `ID: ${sequence.id} - ${sequence.name}`;

  const handleSequenceSaved = async savedSequence => {
    const nextSequences = await fetchSequences();
    const sequenceId = savedSequence?.id || sequenceModalId;
    if (sequenceModalContext && sequenceId) {
      updatePromptBuilderList(
        sequenceModalContext.listName,
        sequenceModalContext.index,
        "sequenceId",
        String(sequenceId)
      );
    }

    if (sequenceId && !nextSequences.some(sequence => String(sequence.id) === String(sequenceId))) {
      setSequences(prevSequences => [...prevSequences, savedSequence].filter(Boolean));
    }

    setSequenceModalOpen(false);
    setSequenceModalContext(null);
    setSequenceModalId(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalContext(null);
    setSequenceModalId(null);
  };

  const getSequenceModalInitialName = () => {
    if (!sequenceModalContext) return "";
    const rowNumber = sequenceModalContext.index + 1;
    return sequenceModalContext.listName === "products"
      ? `Producto #${rowNumber}`
      : sequenceModalContext.listName === "escalations"
      ? `Escalamiento #${rowNumber}`
      : sequenceModalContext.listName === "promptSequences"
      ? `Secuencia #${rowNumber}`
      : `Respuesta #${rowNumber}`;
  };

  const getSequenceModalInitialCategory = () => {
    if (!sequenceModalContext) return "General";
    const categoriesByList = {
      products: "Productos",
      exampleResponses: "Preguntas frecuentes",
      promptSequences: "Secuencias",
      escalations: "Escalamientos",
    };
    return categoriesByList[sequenceModalContext.listName] || "General";
  };

  const promptBuilderFeatureFlags = {
    useOrdersTool: canUseOrders && config.useOrdersTool !== false,
    useSequencesTool: config.useSequencesTool !== false,
    useWebSearchTool: config.useWebSearchTool !== false,
  };
  const showOrderPromptBuilderSections = promptBuilderFeatureFlags.useOrdersTool;
  const showSequencePromptBuilderSections = promptBuilderFeatureFlags.useSequencesTool;
  const showWebSearchPromptBuilderSections = promptBuilderFeatureFlags.useWebSearchTool;

  const applyPromptBuilder = mode => {
    const generatedPrompt = buildPromptFromBuilder(
      promptBuilder,
      orderSettings,
      promptBuilderFeatureFlags
    );
    if (!generatedPrompt) {
      toast.warn("Completa al menos un dato del constructor.");
      return;
    }

    setConfig(prevConfig => ({
      ...prevConfig,
      customPrompt:
        mode === "append" && prevConfig.customPrompt
          ? `${prevConfig.customPrompt.trim()}\n\n${generatedPrompt}`
          : generatedPrompt,
    }));
  };

  const loadDefaultPromptBuilder = () => {
    const nextBuilder = clonePromptBuilder(defaultPromptBuilder);
    setPromptBuilder(nextBuilder);
    setConfig(prevConfig => ({
      ...prevConfig,
      prompt: buildPromptFromBuilder(nextBuilder, orderSettings),
      customPrompt: prevConfig.customPrompt || "",
      naturalMessageModeEnabled: true,
      skipMarkAsRead: false,
      useSequencesTool: true,
      useWasheetsTool: canUseWasheets,
      useWebSearchTool: true,
      useAgendaTool: canUseAgenda,
      useOrdersTool: canUseOrders,
      useWebhookTools: canUseWebhooks,
      useContactTool: true,
      useProcessesTool: false,
      selectedProcessIds: [],
    }));
  };

  const clearPromptBuilder = () => {
    setPromptBuilder(clonePromptBuilder(emptyPromptBuilder));
    setConfig(prevConfig => ({
      ...prevConfig,
      isActive: false,
      prompt: "",
      customPrompt: "",
      waitModeEnabled: false,
      skipMarkAsRead: false,
      directModeEnabled: false,
      naturalMessageModeEnabled: false,
      useSequencesTool: false,
      useWasheetsTool: false,
      useWebSearchTool: false,
      useAgendaTool: false,
      useOrdersTool: false,
      useWebhookTools: false,
      useContactTool: false,
      useProcessesTool: false,
      selectedProcessIds: [],
    }));
  };

  const selectedModel = models.find(model => model.id === config.model);
  const usesOwnApiKey = config.apiKeyMode === "own";
  const usesMcpExecution = config.executionMode === "mcp";
  const isReasoningModel = selectedModel?.family === "reasoning";
  const reasoningEffortOptions = getModelReasoningEfforts(selectedModel);
  const reasoningEffortValue = getValidReasoningEffort(
    selectedModel,
    config.reasoningEffort
  );
  const selectedWhatsappNumber = Number(selectedConnectionId);
  const configWhatsappNumber = Number(config.connectionId || selectedConnectionId);
  const isSharedFromAnother =
    !selectedWhatsappIsOrphan &&
    Boolean(config.connectionId) &&
    configWhatsappNumber !== selectedWhatsappNumber;
  const selectedProcessLabels = selectedIds => {
    const ids = normalizeConnectionIds(selectedIds);
    if (!ids.length) return "Todos los procesos";
    return ids
      .map(id => availableProcesses.find(process => Number(process.id) === Number(id))?.name)
      .filter(Boolean)
      .join(", ");
  };
  const selectedProcessSelectValue = normalizeConnectionIds(config.selectedProcessIds);
  const formatPrice = value =>
    value === null || value === undefined ? "No publicado" : `$${value} / MTok`;

  const handleSave = async () => {
    if (!selectedConnectionId) {
      toast.error("Selecciona una conexion.");
      return;
    }
    if (selectedWhatsappIsOrphan) {
      toast.error("Conecta una nueva conexion para editar esta configuracion.");
      return;
    }

    const promptBuilderToSave = sanitizePromptBuilderSequences(promptBuilder);
    const promptToSave = config.useCustomPrompt
      ? config.customPrompt
      : buildPromptFromBuilder(
          promptBuilderToSave,
          orderSettings,
          promptBuilderFeatureFlags
        );

    setSaving(true);
    try {
      const validConnectionIds = connections.map(connection => Number(connection.id));
      const primaryConnectionId = Number(selectedConnectionId);
      const sharedConnectionIds = normalizeConnectionIds(
        primaryConnectionId,
        config.sharedConnectionIds
      ).filter(connectionId => validConnectionIds.includes(Number(connectionId)));
      const { data } = await api.put("/ai/config", {
        ...config,
        reasoningEffort: reasoningEffortValue,
        prompt: promptToSave,
        customPrompt: config.customPrompt,
        promptBuilder: promptBuilderToSave,
        useAgendaTool: canUseAgenda ? config.useAgendaTool !== false : false,
        useOrdersTool: canUseOrders ? config.useOrdersTool !== false : false,
        useWasheetsTool: canUseWasheets ? config.useWasheetsTool !== false : false,
        useWebhookTools: canUseWebhooks ? config.useWebhookTools !== false : false,
        useProcessesTool: canUseProcesses ? config.useProcessesTool === true : false,
        selectedProcessIds:
          canUseProcesses && config.useProcessesTool === true
            ? normalizeConnectionIds(config.selectedProcessIds)
            : [],
        ...getWhatsappTabParams(selectedConnectionId),
        connectionId: primaryConnectionId,
        sharedConnectionIds,
      });
      setConfig(prevConfig => ({
        ...prevConfig,
        ...data,
        sharedConnectionIds: normalizeConnectionIds(data.sharedConnectionIds, data.connectionId),
      }));
      toast.success(i18n.t("ai.toasts.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar metodo"
        open={Boolean(deletingPaymentMethod)}
        onClose={() => setDeletingPaymentMethod(null)}
        onConfirm={deletePaymentMethod}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={selectedWhatsappIsOrphan ? "" : selectedConnectionId}
        initialSequenceName={getSequenceModalInitialName()}
        initialSequenceCategory={getSequenceModalInitialCategory()}
      />
      <Dialog
        open={paymentMethodModalOpen}
        onClose={() => setPaymentMethodModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {paymentMethodForm.id ? "Editar metodo" : "Nuevo metodo de pago"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre"
            value={paymentMethodForm.name}
            onChange={event =>
              setPaymentMethodForm({ ...paymentMethodForm, name: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Descripcion"
            value={paymentMethodForm.description}
            onChange={event =>
              setPaymentMethodForm({ ...paymentMethodForm, description: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Instrucciones"
            value={paymentMethodForm.instructions}
            onChange={event =>
              setPaymentMethodForm({ ...paymentMethodForm, instructions: event.target.value })
            }
            variant="outlined"
            margin="dense"
            multiline
            minRows={3}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={paymentMethodForm.requiresProof}
                onChange={event =>
                  setPaymentMethodForm({
                    ...paymentMethodForm,
                    requiresProof: event.target.checked,
                  })
                }
              />
            }
            label="Requiere comprobante"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={paymentMethodForm.isActive !== false}
                onChange={event =>
                  setPaymentMethodForm({
                    ...paymentMethodForm,
                    isActive: event.target.checked,
                  })
                }
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPaymentMethodModalOpen(false)}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button onClick={savePaymentMethod} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <MainHeader>
        <Title>{i18n.t("ai.title")}</Title>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.isActive}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      isActive: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label={i18n.t("ai.form.active")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.directModeEnabled}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      directModeEnabled: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label="Directo"
            />
          </Grid>
          {!selectedWhatsappIsOrphan && (
            <Grid item xs={12} md={6}>
              {isSharedFromAnother ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                  {getWhatsappName(connections, config.connectionId)} le está compartiendo.
                </Typography>
              ) : (
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <Typography variant="body2" color="textSecondary">
                    Compartir con:
                  </Typography>
                  {connections
                    .filter(whatsapp => Number(whatsapp.id) !== Number(selectedConnectionId))
                    .map(whatsapp => (
                      <FormControlLabel
                        key={whatsapp.id}
                        control={
                          <Checkbox
                            checked={(config.sharedConnectionIds || [])
                              .map(Number)
                              .includes(Number(whatsapp.id))}
                            onChange={event => {
                              const currentIds = (config.sharedConnectionIds || []).filter(
                                id => Number(id) !== Number(whatsapp.id)
                              );
                              const nextIds = event.target.checked
                                ? [...currentIds, whatsapp.id]
                                : currentIds;
                              setConfig(prevConfig => ({
                                ...prevConfig,
                                connectionId: selectedConnectionId,
                                sharedConnectionIds: normalizeConnectionIds(
                                  selectedConnectionId,
                                  nextIds
                                ),
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                      />
                    ))}
                </div>
              )}
            </Grid>
          )}
          {false && !selectedWhatsappIsOrphan && (
            <>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Conexión</InputLabel>
                <Select
                  value={config.connectionId || selectedConnectionId || ""}
                  label="Conexión"
                  onChange={event => {
                    const primaryId = Number(event.target.value);
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      connectionId: primaryId,
                      sharedConnectionIds: normalizeConnectionIds(
                        primaryId,
                        prevConfig.sharedConnectionIds
                      ),
                    }));
                  }}
                >
                  {connections.map(whatsapp => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      <Typography>
                        {whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Compartir con</InputLabel>
                <Select
                  multiple
                  value={(config.sharedConnectionIds || []).filter(
                    id => Number(id) !== Number(config.connectionId || selectedConnectionId)
                  )}
                  label="Compartir con"
                  renderValue={selected =>
                    selected.length
                      ? selected.map(id => getWhatsappName(connections, id)).join(", ")
                      : "Sin conexiones adicionales"
                  }
                  onChange={event => {
                    const primaryId = config.connectionId || selectedConnectionId;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      connectionId: primaryId,
                      sharedConnectionIds: normalizeConnectionIds(
                        primaryId,
                        event.target.value
                      ),
                    }));
                  }}
                >
                  {connections
                    .filter(
                      whatsapp =>
                        Number(whatsapp.id) !== Number(config.connectionId || selectedConnectionId)
                    )
                    .map(whatsapp => (
                      <MenuItem key={whatsapp.id} value={whatsapp.id}>
                        <Checkbox
                          checked={(config.sharedConnectionIds || []).indexOf(whatsapp.id) > -1}
                        />
                        <Typography>
                          {whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
                        </Typography>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            </>
          )}
          {isSuperAdmin && (
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Tipo de API Key</InputLabel>
                <Select
                  name="apiKeyMode"
                  value={config.apiKeyMode || "included"}
                  onChange={event => {
                    const { value } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      apiKeyMode: value,
                      ...(value === "included"
                        ? {
                            apiKey: "",
                            model: "gpt-4o-mini",
                            temperature: 1,
                            maxOutputTokens: 16384,
                          }
                        : {}),
                    }));
                  }}
                  label="Tipo de API Key"
                >
                  <MenuItem value="included">API key Incluida</MenuItem>
                  <MenuItem value="own">API key Propia</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Motor de IA</InputLabel>
              <Select
                name="executionMode"
                value={config.executionMode || "api"}
                onChange={handleChange}
                label="Motor de IA"
              >
                <MenuItem value="api">API de OpenAI</MenuItem>
                <MenuItem value="mcp">ChatGPT mediante MCP</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {usesMcpExecution && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Conecta ChatGPT a {`${window.location.origin}/mcp`} para atender mensajes y ejecutar las acciones habilitadas.
              </Typography>
            </Grid>
          )}
          {!usesMcpExecution && !usesOwnApiKey && (
            <Grid item xs={12}>
              <div className={classes.modelInfo}>
                <div className={classes.modelInfoHeader}>
                  <Typography variant="subtitle2">API key Incluida</Typography>
                  <Chip
                    size="small"
                    color={
                      aiIncludedCreditUsage?.limit != null &&
                      Number(aiIncludedCreditUsage?.used || 0) >=
                        Number(aiIncludedCreditUsage?.limit || 0)
                        ? "secondary"
                        : "default"
                    }
                    label={`IA ${formatCreditValue(
                      aiIncludedCreditUsage?.used
                    )}/${formatCreditValue(
                      aiIncludedCreditUsage?.limit
                    )} creditos`}
                  />
                </div>
                <Typography variant="body2" color="textSecondary">
                  Tu servicio ya incluye IA lista para usar, sin configurar una API Key propia.
                </Typography>
                <Typography variant="body2">
                  Modelo por defecto: GPT-4o mini · Temperatura: 1 · Tokens maximos: 16384
                </Typography>
              </div>
            </Grid>
          )}
          {!usesMcpExecution && usesOwnApiKey && (
            <>
          <Grid item xs={12} md={6}>
            <TextField
              label={i18n.t("ai.form.apiKey")}
              name="apiKey"
              value={config.apiKey}
              onChange={handleChange}
              variant="outlined"
              margin="dense"
              type="password"
              className={classes.textField}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>{i18n.t("ai.form.model")}</InputLabel>
              <Select name="model" value={config.model} onChange={handleChange} label={i18n.t("ai.form.model")}>
                {models.map(model => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {selectedModel && (
            <Grid item xs={12}>
              <div className={classes.modelInfo}>
                <Typography variant="subtitle2">{selectedModel.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedModel.description}
                </Typography>
                <Grid container spacing={1} className={classes.modelMetaGrid}>
                  <Grid item xs={12} sm={4} md={2}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Entrada</Typography>
                      <Typography variant="body2">{formatPrice(selectedModel.pricing?.input)}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Cache</Typography>
                      <Typography variant="body2">{formatPrice(selectedModel.pricing?.cachedInput)}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Salida</Typography>
                      <Typography variant="body2">{formatPrice(selectedModel.pricing?.output)}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Contexto</Typography>
                      <Typography variant="body2">{selectedModel.capabilities?.contextWindow}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Salida maxima</Typography>
                      <Typography variant="body2">{selectedModel.capabilities?.maxOutput}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Razonamiento</Typography>
                      <Typography variant="body2">{selectedModel.capabilities?.reasoning}</Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <div className={classes.modelMetaItem}>
                      <Typography variant="caption" color="textSecondary">Capacidades</Typography>
                      <Typography variant="body2">{selectedModel.capabilities?.modalities}</Typography>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          )}
          {isReasoningModel ? (
            <>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Formato</InputLabel>
                  <Select name="textFormat" value={config.textFormat || "text"} onChange={handleChange} label="Formato">
                    <MenuItem value="text">text</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Effort</InputLabel>
                  <Select name="reasoningEffort" value={reasoningEffortValue} onChange={handleChange} label="Effort">
                    {reasoningEffortOptions.map(effort => (
                      <MenuItem key={effort} value={effort}>{effort}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Verbosity</InputLabel>
                  <Select name="verbosity" value={config.verbosity || "medium"} onChange={handleChange} label="Verbosity">
                    <MenuItem value="low">low</MenuItem>
                    <MenuItem value="medium">medium</MenuItem>
                    <MenuItem value="high">high</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>Summary</InputLabel>
                  <Select name="summary" value={config.summary || "auto"} onChange={handleChange} label="Summary">
                    <MenuItem value="auto">auto</MenuItem>
                    <MenuItem value="concise">concise</MenuItem>
                    <MenuItem value="detailed">detailed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Temperatura"
                  name="temperature"
                  value={config.temperature}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  type="number"
                  inputProps={{ min: 0, max: 2, step: 0.01 }}
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Tokens maximos"
                  name="maxOutputTokens"
                  value={config.maxOutputTokens}
                  onChange={handleChange}
                  variant="outlined"
                  margin="dense"
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  className={classes.textField}
                />
              </Grid>
            </>
          )}
            </>
          )}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.skipMarkAsRead)}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      skipMarkAsRead: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label={i18n.t("ai.form.skipMarkAsRead")}
            />
          </Grid>
          <Grid item xs={12}>
            <div className={classes.aiBehaviorGrid}>
              <div className={classes.aiBehaviorColumn}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={config.waitModeEnabled}
                      onChange={event => {
                        const { checked } = event.target;
                        setConfig(prevConfig => ({
                          ...prevConfig,
                          waitModeEnabled: checked,
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={i18n.t("ai.form.waitModeEnabled")}
                />
                {config.waitModeEnabled && (
                  <TextField
                    label={i18n.t("ai.form.waitModeSeconds")}
                    name="waitModeSeconds"
                    value={config.waitModeSeconds}
                    onChange={handleChange}
                    variant="outlined"
                    margin="dense"
                    type="number"
                    inputProps={{ min: 1 }}
                    className={classes.textField}
                  />
                )}
              </div>
              <div className={classes.aiBehaviorColumn}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(config.applyConversationContext)}
                      onChange={event => {
                        const { checked } = event.target;
                        setConfig(prevConfig => ({
                          ...prevConfig,
                          applyConversationContext: checked,
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={i18n.t("ai.form.applyConversationContext")}
                />
                {config.applyConversationContext && (
                  <TextField
                    label={i18n.t("ai.form.conversationContextMessages")}
                    name="conversationContextMessages"
                    value={config.conversationContextMessages}
                    onChange={handleChange}
                    variant="outlined"
                    margin="dense"
                    type="number"
                    inputProps={{ min: 1, max: 100 }}
                    className={classes.textField}
                  />
                )}
              </div>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2">Funciones</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(config.naturalMessageModeEnabled)}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      naturalMessageModeEnabled: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label="Mensajes naturales"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.useSequencesTool !== false}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      useSequencesTool: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label="Secuencias"
            />
          </Grid>
          {canUseWasheets && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useWasheetsTool !== false}
                    onChange={event => {
                      const { checked } = event.target;
                      setConfig(prevConfig => ({
                        ...prevConfig,
                        useWasheetsTool: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Formularios Google Sheets"
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.useWebSearchTool !== false}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      useWebSearchTool: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label="Búsqueda web"
            />
          </Grid>
          {canUseAgenda && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useAgendaTool !== false}
                    onChange={event => {
                      const { checked } = event.target;
                      setConfig(prevConfig => ({
                        ...prevConfig,
                        useAgendaTool: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Agendamiento"
              />
            </Grid>
          )}
          {canUseOrders && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useOrdersTool !== false}
                    onChange={event => {
                      const { checked } = event.target;
                      setConfig(prevConfig => ({
                        ...prevConfig,
                        useOrdersTool: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Pedidos"
              />
            </Grid>
          )}
          {canUseWebhooks && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useWebhookTools !== false}
                    onChange={event => {
                      const { checked } = event.target;
                      setConfig(prevConfig => ({
                        ...prevConfig,
                        useWebhookTools: checked,
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Webhooks IA"
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={config.useContactTool !== false}
                  onChange={event => {
                    const { checked } = event.target;
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      useContactTool: checked,
                    }));
                  }}
                  color="primary"
                />
              }
              label="Datos de Contacto"
            />
          </Grid>
          {canUseProcesses && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={config.useProcessesTool === true}
                    onChange={event => {
                      const { checked } = event.target;
                      setConfig(prevConfig => ({
                        ...prevConfig,
                        useProcessesTool: checked,
                        selectedProcessIds: checked
                          ? prevConfig.selectedProcessIds || []
                          : [],
                      }));
                    }}
                    color="primary"
                  />
                }
                label="Procesos IA"
              />
            </Grid>
          )}
          {canUseProcesses && config.useProcessesTool === true && (
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel>Procesos a listar</InputLabel>
                <Select
                  multiple
                  label="Procesos a listar"
                  value={
                    selectedProcessSelectValue.length
                      ? selectedProcessSelectValue
                      : [ALL_AI_PROCESSES]
                  }
                  renderValue={selected => selectedProcessLabels(selected)}
                  onChange={event =>
                    setConfig(prevConfig => ({
                      ...prevConfig,
                      selectedProcessIds: normalizeConnectionIds(event.target.value),
                    }))
                  }
                >
                  <MenuItem value={ALL_AI_PROCESSES} disabled>
                    <Checkbox
                      checked={!selectedProcessSelectValue.length}
                      color="primary"
                    />
                    <ListItemText primary="Todos los procesos" />
                  </MenuItem>
                  {availableProcesses.map(process => (
                    <MenuItem key={process.id} value={Number(process.id)}>
                      <Checkbox
                        checked={normalizeConnectionIds(config.selectedProcessIds).includes(
                          Number(process.id)
                        )}
                        color="primary"
                      />
                      <ListItemText primary={process.name} />
                    </MenuItem>
                  ))}
                  {!availableProcesses.length && (
                    <MenuItem disabled>No hay procesos disponibles</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <div className={classes.promptBuilder}>
              <div className={classes.promptBuilderHeader}>
                <div className={classes.promptBuilderTitle}>
                  <Typography variant="subtitle1">Constructor de prompt</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Completa las secciones utiles para tu negocio y genera un prompt listo para nuestra IA.
                  </Typography>
                </div>
                <div className={classes.promptBuilderHeaderActions}>
                  <Button variant="outlined" color="secondary" onClick={clearPromptBuilder}>
                    Limpiar
                  </Button>
                  <Button variant="outlined" color="primary" onClick={loadDefaultPromptBuilder}>
                    Por Defecto
                  </Button>
                </div>
              </div>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Perfil del negocio</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Nombre del negocio"
                    name="businessName"
                    value={promptBuilder.businessName}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Categoria"
                    name="businessCategory"
                    value={promptBuilder.businessCategory}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Idiomas"
                    name="languages"
                    value={promptBuilder.languages}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Descripcion"
                    name="businessSummary"
                    value={promptBuilder.businessSummary}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Direcciones"
                    name="address"
                    value={promptBuilder.address}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Sitios web"
                    name="website"
                    value={promptBuilder.website}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Telefonos"
                    name="phones"
                    value={promptBuilder.phones}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Redes sociales"
                    name="socialNetworks"
                    value={promptBuilder.socialNetworks}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Correos"
                    name="emails"
                    value={promptBuilder.emails}
                    onChange={handlePromptBuilderChange}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={3}
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Horario disponible</Typography>
                </Grid>
                {promptBuilder.businessHours.map(day => (
                  <Grid item xs={12} md={6} key={day.key}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={day.enabled}
                              onChange={event =>
                                updateBusinessHour(day.key, "enabled", event.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label={day.label}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          label="Apertura"
                          type="time"
                          value={day.open}
                          onChange={event =>
                            updateBusinessHour(day.key, "open", event.target.value)
                          }
                          variant="outlined"
                          margin="dense"
                          disabled={!day.enabled}
                          className={classes.textField}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <TextField
                          label="Cierre"
                          type="time"
                          value={day.close}
                          onChange={event =>
                            updateBusinessHour(day.key, "close", event.target.value)
                          }
                          variant="outlined"
                          margin="dense"
                          disabled={!day.enabled}
                          className={classes.textField}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <div className={classes.promptBuilderSection}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Personalidad de la IA</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Nombre del asistente"
                      name="assistantName"
                      value={promptBuilder.assistantName}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Tono"
                      name="tone"
                      value={promptBuilder.tone}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Longitud de respuesta"
                      name="responseLength"
                      value={promptBuilder.responseLength}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Uso de emojis"
                      name="emojiUsage"
                      value={promptBuilder.emojiUsage}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Formalidad"
                      name="formality"
                      value={promptBuilder.formality}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Estilo comercial"
                      name="salesStyle"
                      value={promptBuilder.salesStyle}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                    />
                  </Grid>
                </Grid>
              </div>
              {showOrderPromptBuilderSections && (
                <>
                  <div className={classes.promptBuilderSection}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Informacion de compra</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Instrucciones de compra"
                      name="purchaseInstructions"
                      value={promptBuilder.purchaseInstructions}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={3}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.quickAdminHeader}>
                      <Typography variant="subtitle1">Metodos de pago</Typography>
                      <Button
                        color="primary"
                        variant="outlined"
                        startIcon={<Payment />}
                        onClick={() => openPaymentMethodModal()}
                      >
                        Agregar
                      </Button>
                    </div>
                    <div className={classes.quickAdminTable}>
                      <Table size="small">
                        <TableBody>
                          {paymentMethods.map(method => (
                            <TableRow key={method.id}>
                              <TableCell>
                                <Typography variant="body2">{method.name}</Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {method.requiresProof
                                    ? "Requiere comprobante"
                                    : "Sin comprobante obligatorio"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => openPaymentMethodModal(method)}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeletingPaymentMethod(method)}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          {!paymentMethods.length && (
                            <TableRow>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">
                                  Sin metodos de pago configurados
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Descuentos"
                      name="discounts"
                      value={promptBuilder.discounts}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={3}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Politica de envio"
                      name="shippingPolicy"
                      value={promptBuilder.shippingPolicy}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={3}
                      className={classes.textField}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Politica de devoluciones y cambios"
                      name="returnsPolicy"
                      value={promptBuilder.returnsPolicy}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={3}
                      className={classes.textField}
                    />
                  </Grid>
                </Grid>
              </div>
              <div className={classes.promptBuilderSection}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Productos</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {showSequencePromptBuilderSections
                        ? "Agrega la informacion del producto y la secuencia que debe ejecutarse cuando aplique."
                        : "Agrega la informacion del producto."}
                    </Typography>
                  </Grid>
                  {promptBuilder.products.map((product, index) => (
                    <Grid item xs={12} md={6} lg={4} key={`product-${index}`}>
                      <div className={classes.promptBuilderCard}>
                        <div className={classes.promptBuilderCardHeader}>
                          <Typography variant="subtitle2">Producto #{index + 1}</Typography>
                          <Button
                            color="secondary"
                            onClick={() => removePromptBuilderListItem("products", index)}
                          >
                            Quitar
                          </Button>
                        </div>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              label="Producto"
                              value={product.name}
                              onChange={event =>
                                updatePromptBuilderList("products", index, "name", event.target.value)
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Informacion del producto"
                              value={product.description}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "products",
                                  index,
                                  "description",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Precio"
                              value={product.price}
                              onChange={event =>
                                updatePromptBuilderList("products", index, "price", event.target.value)
                              }
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Precio oferta"
                              value={product.offerPrice}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "products",
                                  index,
                                  "offerPrice",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" className={classes.sectionTitle}>
                              Variaciones simples
                            </Typography>
                            {(product.variants || []).map((variant, variantIndex) => (
                              <div className={classes.rowFields} key={`product-${index}-variant-${variantIndex}`}>
                                <TextField
                                  label="Variacion"
                                  value={variant.name || ""}
                                  onChange={event =>
                                    updateProductVariant(
                                      index,
                                      variantIndex,
                                      "name",
                                      event.target.value
                                    )
                                  }
                                  variant="outlined"
                                  margin="dense"
                                />
                                <TextField
                                  label="Precio"
                                  value={variant.price || ""}
                                  onChange={event =>
                                    updateProductVariant(
                                      index,
                                      variantIndex,
                                      "price",
                                      event.target.value
                                    )
                                  }
                                  variant="outlined"
                                  margin="dense"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => removeProductVariant(index, variantIndex)}
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </div>
                            ))}
                            <Button
                              color="primary"
                              startIcon={<Add />}
                              onClick={() => addProductVariant(index)}
                            >
                              Agregar variacion
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Respuesta"
                              value={product.answer || ""}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "products",
                                  index,
                                  "answer",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          {showSequencePromptBuilderSections && (
                            <Grid item xs={12}>
                              <FormControl variant="outlined" margin="dense" className={classes.textField}>
                              <InputLabel>Secuencia</InputLabel>
                              <Select
                                value={product.sequenceId || NO_SEQUENCE}
                                onChange={event => {
                                  if (event.target.value === "__create__") {
                                    openCreateSequence("products", index);
                                    return;
                                  }

                                  updatePromptBuilderList(
                                    "products",
                                    index,
                                    "sequenceId",
                                    event.target.value
                                  );
                                }}
                                label="Secuencia"
                              >
                                <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                                <MenuItem value="__create__">Crear secuencia...</MenuItem>
                                {sequences.map(sequence => (
                                  <MenuItem key={sequence.id} value={String(sequence.id)}>
                                    {formatSequenceOption(sequence)}
                                  </MenuItem>
                                ))}
                              </Select>
                              </FormControl>
                              <div className={classes.sequenceActions}>
                                <Button
                                  color="primary"
                                  disabled={!product.sequenceId || product.sequenceId === NO_SEQUENCE}
                                  onClick={() =>
                                    openEditSequence("products", index, product.sequenceId)
                                  }
                                >
                                  Editar secuencia
                                </Button>
                              </div>
                            </Grid>
                          )}
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => addPromptBuilderListItem("products")}
                    >
                      Agregar producto
                    </Button>
                  </Grid>
                </Grid>
              </div>
                </>
              )}

              <div className={classes.promptBuilderSection}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Preguntas frecuentes</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {showSequencePromptBuilderSections
                        ? "Agrega preguntas, respuestas exactas y la secuencia que la IA debe ejecutar cuando corresponda."
                        : "Agrega preguntas y respuestas exactas."}
                    </Typography>
                  </Grid>
                  {promptBuilder.exampleResponses.map((item, index) => (
                    <Grid item xs={12} md={6} lg={4} key={`example-${index}`}>
                      <div className={classes.promptBuilderCard}>
                        <div className={classes.promptBuilderCardHeader}>
                          <Typography variant="subtitle2">Pregunta frecuente #{index + 1}</Typography>
                          <Button
                            color="secondary"
                            onClick={() => removePromptBuilderListItem("exampleResponses", index)}
                          >
                            Quitar
                          </Button>
                        </div>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              label="Pregunta"
                              value={item.question}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "exampleResponses",
                                  index,
                                  "question",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Respuesta"
                              value={item.answer}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "exampleResponses",
                                  index,
                                  "answer",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          {showSequencePromptBuilderSections && (
                            <Grid item xs={12}>
                              <FormControl variant="outlined" margin="dense" className={classes.textField}>
                              <InputLabel>Secuencia</InputLabel>
                              <Select
                                value={item.sequenceId || NO_SEQUENCE}
                                onChange={event => {
                                  if (event.target.value === "__create__") {
                                    openCreateSequence("exampleResponses", index);
                                    return;
                                  }

                                  updatePromptBuilderList(
                                    "exampleResponses",
                                    index,
                                    "sequenceId",
                                    event.target.value
                                  );
                                }}
                                label="Secuencia"
                              >
                                <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                                <MenuItem value="__create__">Crear secuencia...</MenuItem>
                                {sequences.map(sequence => (
                                  <MenuItem key={sequence.id} value={String(sequence.id)}>
                                    {formatSequenceOption(sequence)}
                                  </MenuItem>
                                ))}
                              </Select>
                              </FormControl>
                              <div className={classes.sequenceActions}>
                                <Button
                                  color="primary"
                                  disabled={!item.sequenceId || item.sequenceId === NO_SEQUENCE}
                                  onClick={() =>
                                    openEditSequence("exampleResponses", index, item.sequenceId)
                                  }
                                >
                                  Editar secuencia
                                </Button>
                              </div>
                            </Grid>
                          )}
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => addPromptBuilderListItem("exampleResponses")}
                    >
                      Agregar respuesta
                    </Button>
                  </Grid>
                </Grid>
              </div>

              {showSequencePromptBuilderSections && (
                <div className={classes.promptBuilderSection}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Secuencias</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Define secuencias disponibles por nombre para que la IA las tenga como referencia.
                    </Typography>
                  </Grid>
                  {promptBuilder.promptSequences.map((item, index) => (
                    <Grid item xs={12} md={6} lg={4} key={`prompt-sequence-${index}`}>
                      <div className={classes.promptBuilderCard}>
                        <div className={classes.promptBuilderCardHeader}>
                          <Typography variant="subtitle2">Secuencia #{index + 1}</Typography>
                          <Button
                            color="secondary"
                            onClick={() => removePromptBuilderListItem("promptSequences", index)}
                          >
                            Quitar
                          </Button>
                        </div>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              label="Nombre"
                              value={item.name}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "promptSequences",
                                  index,
                                  "name",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          {showSequencePromptBuilderSections && (
                            <Grid item xs={12}>
                              <FormControl variant="outlined" margin="dense" className={classes.textField}>
                              <InputLabel>Secuencia</InputLabel>
                              <Select
                                value={item.sequenceId || NO_SEQUENCE}
                                onChange={event => {
                                  if (event.target.value === "__create__") {
                                    openCreateSequence("promptSequences", index);
                                    return;
                                  }

                                  updatePromptBuilderList(
                                    "promptSequences",
                                    index,
                                    "sequenceId",
                                    event.target.value
                                  );
                                }}
                                label="Secuencia"
                              >
                                <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                                <MenuItem value="__create__">Crear secuencia...</MenuItem>
                                {sequences.map(sequence => (
                                  <MenuItem key={sequence.id} value={String(sequence.id)}>
                                    {formatSequenceOption(sequence)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <div className={classes.sequenceActions}>
                              <Button
                                color="primary"
                                disabled={!item.sequenceId || item.sequenceId === NO_SEQUENCE}
                                onClick={() =>
                                  openEditSequence("promptSequences", index, item.sequenceId)
                                }
                              >
                                Editar secuencia
                              </Button>
                            </div>
                          </Grid>
                          )}
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => addPromptBuilderListItem("promptSequences")}
                    >
                      Agregar secuencia
                    </Button>
                  </Grid>
                  </Grid>
                </div>
              )}

              <div className={classes.promptBuilderSection}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Escalamientos</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {showSequencePromptBuilderSections
                        ? "Define cuando escalar y que secuencia debe ejecutarse."
                        : "Define cuando escalar y que respuesta debe enviarse."}
                    </Typography>
                  </Grid>
                  {promptBuilder.escalations.map((item, index) => (
                    <Grid item xs={12} md={6} lg={4} key={`escalation-${index}`}>
                      <div className={classes.promptBuilderCard}>
                        <div className={classes.promptBuilderCardHeader}>
                          <Typography variant="subtitle2">Escalamiento #{index + 1}</Typography>
                          <Button
                            color="secondary"
                            onClick={() => removePromptBuilderListItem("escalations", index)}
                          >
                            Quitar
                          </Button>
                        </div>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              label="Cuando"
                              value={item.escalation}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "escalations",
                                  index,
                                  "escalation",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Respuesta"
                              value={item.answer || ""}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "escalations",
                                  index,
                                  "answer",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          {showSequencePromptBuilderSections && (
                            <Grid item xs={12}>
                              <FormControl variant="outlined" margin="dense" className={classes.textField}>
                              <InputLabel>Secuencia</InputLabel>
                              <Select
                                value={item.sequenceId || NO_SEQUENCE}
                                onChange={event => {
                                  if (event.target.value === "__create__") {
                                    openCreateSequence("escalations", index);
                                    return;
                                  }

                                  updatePromptBuilderList(
                                    "escalations",
                                    index,
                                    "sequenceId",
                                    event.target.value
                                  );
                                }}
                                label="Secuencia"
                              >
                                <MenuItem value={NO_SEQUENCE}>Sin secuencia</MenuItem>
                                <MenuItem value="__create__">Crear secuencia...</MenuItem>
                                {sequences.map(sequence => (
                                  <MenuItem key={sequence.id} value={String(sequence.id)}>
                                    {formatSequenceOption(sequence)}
                                  </MenuItem>
                                ))}
                              </Select>
                              </FormControl>
                              <div className={classes.sequenceActions}>
                                <Button
                                  color="primary"
                                  disabled={!item.sequenceId || item.sequenceId === NO_SEQUENCE}
                                  onClick={() =>
                                    openEditSequence("escalations", index, item.sequenceId)
                                  }
                                >
                                  Editar secuencia
                                </Button>
                              </div>
                            </Grid>
                          )}
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => addPromptBuilderListItem("escalations")}
                    >
                      Agregar escalamiento
                    </Button>
                  </Grid>
                </Grid>
              </div>

              {showWebSearchPromptBuilderSections && (
                <div className={classes.promptBuilderSection}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Base de conocimientos</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Agrega preguntas, sitios a visitar y respuestas directas para la IA.
                    </Typography>
                  </Grid>
                  {(Array.isArray(promptBuilder.knowledgeBase)
                    ? promptBuilder.knowledgeBase
                    : [emptyKnowledgeBaseItem()]
                  ).map((item, index) => (
                    <Grid item xs={12} md={6} lg={4} key={`knowledge-${index}`}>
                      <div className={classes.promptBuilderCard}>
                        <div className={classes.promptBuilderCardHeader}>
                          <Typography variant="subtitle2">Conocimiento #{index + 1}</Typography>
                          <Button
                            color="secondary"
                            onClick={() => removePromptBuilderListItem("knowledgeBase", index)}
                          >
                            Quitar
                          </Button>
                        </div>
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <TextField
                              label="Pregunta"
                              value={item.question}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "knowledgeBase",
                                  index,
                                  "question",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Sitio a visitar"
                              value={item.site}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "knowledgeBase",
                                  index,
                                  "site",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              label="Respuesta"
                              value={item.answer}
                              onChange={event =>
                                updatePromptBuilderList(
                                  "knowledgeBase",
                                  index,
                                  "answer",
                                  event.target.value
                                )
                              }
                              variant="outlined"
                              margin="dense"
                              multiline
                              minRows={4}
                              className={classes.textField}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => addPromptBuilderListItem("knowledgeBase")}
                    >
                      Agregar conocimiento
                    </Button>
                  </Grid>
                  </Grid>
                </div>
              )}

              <div className={classes.promptBuilderSection}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Flujo y reglas</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Define como debe avanzar la IA y las reglas generales de respuesta.
                    </Typography>
                  </Grid>
                  {showOrderPromptBuilderSections && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Flujo recomendado para pedidos"
                        name="orderFlow"
                        value={promptBuilder.orderFlow}
                        onChange={handlePromptBuilderChange}
                        variant="outlined"
                        margin="dense"
                        multiline
                        minRows={6}
                        className={classes.textField}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={showOrderPromptBuilderSections ? 6 : 12}>
                    <TextField
                      label="Reglas generales de respuesta"
                      name="generalResponseRules"
                      value={promptBuilder.generalResponseRules}
                      onChange={handlePromptBuilderChange}
                      variant="outlined"
                      margin="dense"
                      multiline
                      minRows={6}
                      className={classes.textField}
                    />
                  </Grid>
                </Grid>
              </div>

              <div className={classes.promptBuilderActions}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={Boolean(config.useCustomPrompt)}
                      onChange={handleChange}
                      name="useCustomPrompt"
                    />
                  }
                  label="Prompt Personalizado"
                />
                {config.useCustomPrompt && (
                  <>
                    <Button variant="contained" color="primary" onClick={() => applyPromptBuilder("replace")}>
                      Generar prompt
                    </Button>
                    <Button variant="outlined" color="primary" onClick={() => applyPromptBuilder("append")}>
                      Agregar al prompt actual
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Grid>
          {config.useCustomPrompt && (
            <Grid item xs={12}>
              <TextField
                label={i18n.t("ai.form.prompt")}
                name="customPrompt"
                value={config.customPrompt}
                onChange={handleChange}
                variant="outlined"
                margin="dense"
                multiline
                minRows={12}
                className={classes.textField}
              />
            </Grid>
          )}
        </Grid>
      </Paper>
      <div className={classes.fixedSaveBar}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || loading || selectedWhatsappIsOrphan}
          className={classes.btnWrapper}
        >
          {i18n.t("ai.buttons.save")}
          {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Button>
      </div>
    </MainContainer>
  );
};

export default Ai;

